import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import SecurityIcon from '@mui/icons-material/Security';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIButton } from '../../../atomic/atoms';
import RoleApprovalQueryService from '../../../services/query/roleApproval/RoleApprovalQuery.services';
import RoleApprovalCommandService from '../../../services/command/roleApproval/RoleApprovalCommand.services';
import Swal from 'sweetalert2';
import Helper from '../../../utils/helper';
import { PageUrl } from '../../../types/enums';

interface RequestableRole {
    roleId: number;
    roleNameKey: string;
    description?: string;
    isRequestable: boolean;
}

export const RequestRole: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [requestableRoles, setRequestableRoles] = useState<RequestableRole[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState<RequestableRole | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [requestNote, setRequestNote] = useState('');
    const [filterText, setFilterText] = useState('');

    // Redirect to home if user has storeCode
    useEffect(() => {
        if (Helper.getStoreCode()) {
            history.push(PageUrl.home.url);
        }
    }, [history]);

    const fetchRequestableRoles = async () => {
        setLoading(true);
        try {
            const response = await RoleApprovalQueryService.GetRequestableRoles();

            if (response) {
                setRequestableRoles(response);
            }
        } catch (error) {
            console.error('Error fetching requestable roles:', error);
            Swal.fire(t('general.error'), t('roleApproval.errorFetchingRoles'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestRole = (role: RequestableRole) => {
        setSelectedRole(role);
        setOpenDialog(true);
        setRequestNote('');
    };

    const handleSubmitRequest = async () => {
        if (!selectedRole) return;

        try {
            let response = await RoleApprovalCommandService.CreateUserRoleApprovalRequest({
                RequestedRoleId: selectedRole.roleId,
                RequestNote: requestNote,
                CountryCode: Helper.getCountryCode(),
                RequestedUserRef: Helper.getUserRef(),
                url: `${import.meta.env.VITE_APPURL}/role-approval/pending-requests`,
            });
            if (response.statuscode === 200 || response.statuscode === 201) {
                Swal.fire(t('general.success'), t('roleApproval.requestSubmittedSuccess'), 'success');
            } else {
                Swal.fire(t('general.error'), t(`roleApproval.${response.error}`), 'error');
            }

            setOpenDialog(false);
            setSelectedRole(null);
            setRequestNote('');
        } catch (error) {
            console.error('Error submitting role request:', error);
            Swal.fire(t('general.error'), t('roleApproval.errorSubmittingRequest'), 'error');
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRole(null);
        setRequestNote('');
    };

    // Filtered roles based on search text
    const filteredRoles = requestableRoles.filter((role) => {
        const searchText = filterText.toLowerCase();
        const roleName = role.roleNameKey.toLowerCase();
        const description = role.description?.toLowerCase() || '';
        const genericDescription = t('roleApproval.genericRoleDescription').toLowerCase();

        return (
            roleName.includes(searchText) ||
            description.includes(searchText) ||
            (!role.description && genericDescription.includes(searchText))
        );
    });

    useEffect(() => {
        fetchRequestableRoles();
    }, []);

    return (
        <SCUIContent>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    {t('roleApproval.requestRoleTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {t('roleApproval.requestRoleDescription')}
                </Typography>

                {/* Search Filter */}
                <Box sx={{ mb: 4 }}>
                    <TextField
                        fullWidth
                        placeholder="Rol ismi veya açıklamasına göre ara..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            maxWidth: 500,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: 'white',
                                },
                            },
                        }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <Typography>{t('general.loading')}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {filteredRoles.map((role) => (
                            <Grid
                                key={role.roleId}
                                sx={{ mb: 3 }}
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                }}
                            >
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease-in-out',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                >
                                    {/* Header with role icon */}
                                    <Box
                                        sx={{
                                            backgroundColor: '#2563eb',
                                            p: 2,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <SecurityIcon sx={{ fontSize: 24 }} />
                                        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                            {role.roleNameKey}
                                        </Typography>
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        {/* Description with icon */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                                mb: 3,
                                                minHeight: '60px', // Sabit minimum yükseklik
                                            }}
                                        >
                                            {role.description && (
                                                <>
                                                    <InfoIcon sx={{ color: 'primary.main', mt: 0.5, fontSize: 20 }} />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ lineHeight: 1.6, fontWeight: 'bold' }}
                                                    >
                                                        {role.description}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </CardContent>

                                    <Box sx={{ p: 3, pt: 0 }}>
                                        <SCUIButton
                                            type="secondary"
                                            sizeType="default"
                                            size="s"
                                            text={t('roleApproval.requestThisRole')}
                                            fullwidth={true}
                                            onClick={() => handleRequestRole(role)}
                                        />
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {!loading && requestableRoles.length === 0 && (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('roleApproval.noRequestableRoles')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('roleApproval.noRequestableRolesDescription')}
                        </Typography>
                    </Box>
                )}

                {!loading && requestableRoles.length > 0 && filteredRoles.length === 0 && (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            Arama kriterlerinize uygun rol bulunamadı
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Farklı anahtar kelimeler ile tekrar deneyin
                        </Typography>
                    </Box>
                )}
            </Box>
            {/* Request Role Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('roleApproval.requestRoleDialog.title')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('roleApproval.requestRoleDialog.confirmMessage')}{' '}
                        <strong>{selectedRole?.roleNameKey}</strong>
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={t('roleApproval.requestNote')}
                        placeholder={t('roleApproval.requestNotePlaceholder')}
                        value={requestNote}
                        onChange={(e) => setRequestNote(e.target.value)}
                        sx={{ mt: 2 }}
                    />

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('roleApproval.requestRoleDialog.approvalNote')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-end', gap: 2, p: 2 }}>
                    <SCUIButton
                        type="secondary"
                        buttonType="button"
                        sizeType="fixed"
                        size="m"
                        text={t('general.cancel')}
                        onClick={handleCloseDialog}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        text={t('roleApproval.submitRequest')}
                        onClick={handleSubmitRequest}
                    />
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
};
